import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import TheChiSo from "components/TheChiSo";
import { oTieuDeCot, AN_DUOI_MD, chuXuongDong } from "lib/kieuBangDuLieu";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogPaperFormDai,
} from "lib/dungDialogFullManHinh";
import { LAYOUT, SEMANTIC } from "assets/theme/avocadoBrand";
import { lichSuKhoApi } from "api/lichSuKho";
import useTour from "hooks/useTour";
import buocLichSuKho from "tours/lichSuKho";

function ngayISO(date) {
  return date.toISOString().slice(0, 10);
}

function homNay() {
  return ngayISO(new Date());
}

function gioHienThi(iso) {
  return new Date(iso).toLocaleString("vi-VN");
}

// Nhập và xuất trong cùng 1 chu kỳ có thể do 2 người khác nhau thực hiện
// (nhân viên kho ca sáng nhập, ca chiều xuất) — hiện đủ cả 2 tên thay vì chỉ
// giữ 1 tên rồi mất dấu vết người còn lại.
function tenNguoiGop(nhap, xuat) {
  const tenNhap = nhap?.tenNguoiThucHien;
  const tenXuat = xuat?.tenNguoiThucHien;
  if (tenNhap && tenXuat) {
    return tenNhap === tenXuat ? tenNhap : `Nhập: ${tenNhap} — Xuất: ${tenXuat}`;
  }
  return tenNhap ?? tenXuat ?? "—";
}

function OChenhLech({ giaTri }) {
  if (giaTri == null) return "—";
  return (
    <MDTypography
      variant="button"
      fontWeight="medium"
      color={giaTri === 0 ? "text" : giaTri < 0 ? "error" : "info"}
    >
      {giaTri > 0 ? "+" : ""}
      {giaTri} kg
    </MDTypography>
  );
}

OChenhLech.propTypes = {
  giaTri: PropTypes.number,
};

OChenhLech.defaultProps = {
  giaTri: null,
};

// Dưới md các cột cân nặng bị ẩn — gom cả chặng đường của Sọt thành một
// dòng nhỏ dưới mã Sọt để không mất dữ liệu, xem lib/kieuBangDuLieu.
function ThongTinPhu({ nhom }) {
  const goc = nhom.xuat ?? nhom.nhap;
  const chang = [
    goc.khoiLuongThuMua != null ? `${goc.khoiLuongThuMua} kg` : null,
    goc.khoiLuongCanLai != null ? `${goc.khoiLuongCanLai} kg` : null,
    nhom.xuat?.khoiLuongXuat != null ? `${nhom.xuat.khoiLuongXuat} kg` : null,
  ].filter(Boolean);
  const phan = [gioHienThi(goc.thoiGian), chang.join(" → "), tenNguoiGop(nhom.nhap, nhom.xuat)];
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
      sx={chuXuongDong}
    >
      {phan.filter(Boolean).join(" · ")}
    </MDTypography>
  );
}

ThongTinPhu.propTypes = {
  nhom: PropTypes.shape({
    nhap: PropTypes.shape({ tenNguoiThucHien: PropTypes.string }),
    xuat: PropTypes.shape({ khoiLuongXuat: PropTypes.number }),
  }).isRequired,
};

function DongSuKien({ su }) {
  const laXuat = su.loai === "xuat";
  return (
    <MDBox py={1.5}>
      <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
        <Chip
          size="small"
          label={laXuat ? "Xuất kho" : "Nhập kho"}
          color={laXuat ? "warning" : "success"}
        />
        <MDTypography variant="caption" color="text">
          {gioHienThi(su.thoiGian)}
        </MDTypography>
      </MDBox>
      <MDTypography variant="button" display="block">
        {laXuat ? (
          <>
            {su.khoiLuongCanLai ?? "—"} kg (lúc nhập) → <b>{su.khoiLuongXuat} kg</b> (lúc xuất)
          </>
        ) : (
          <>
            {su.khoiLuongThuMua} kg (lúc thu mua) → <b>{su.khoiLuongCanLai} kg</b> (cân lại)
          </>
        )}
      </MDTypography>
      <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
        <MDTypography variant="caption" color="text">
          Chênh lệch:
        </MDTypography>
        <OChenhLech giaTri={su.chenhLech} />
      </MDBox>
      <MDTypography variant="caption" color="text" display="block" mt={0.5} sx={chuXuongDong}>
        Người thực hiện: {su.tenNguoiThucHien ?? "—"}
      </MDTypography>
    </MDBox>
  );
}

DongSuKien.propTypes = {
  su: PropTypes.shape({
    loai: PropTypes.string,
    thoiGian: PropTypes.string,
    khoiLuongThuMua: PropTypes.number,
    khoiLuongCanLai: PropTypes.number,
    khoiLuongXuat: PropTypes.number,
    chenhLech: PropTypes.number,
    tenNguoiThucHien: PropTypes.string,
  }).isRequired,
};

// Mỗi DongSuKien chỉ so sánh 2 bước liền kề (thu mua→cân lại, hoặc
// nhập→xuất) — không tự thấy được hao hụt CẢ chu kỳ từ lúc thu mua ngoài
// vườn tới lúc xuất kho bán đi. Gộp riêng 1 dòng tổng quan ở đầu popup để trả
// lời thẳng câu đó, chỉ hiện khi chu kỳ đã có cả 2 đầu (đã xuất kho rồi).
function TongQuanChuKy({ chiTietChuKy }) {
  const suNhap = chiTietChuKy.find((su) => su.loai === "nhap");
  const suXuat = chiTietChuKy.find((su) => su.loai === "xuat");
  if (!suNhap || !suXuat) return null;

  const chenhLech = suXuat.khoiLuongXuat - suNhap.khoiLuongThuMua;
  const tyLe = suNhap.khoiLuongThuMua > 0 ? (chenhLech / suNhap.khoiLuongThuMua) * 100 : 0;

  return (
    <MDBox
      mb={1.5}
      px={1.75}
      py={1.25}
      borderRadius="lg"
      sx={({ palette }) => ({
        backgroundColor: palette.grey[100],
        borderLeft: `3px solid ${palette.info.main}`,
      })}
    >
      <MDTypography variant="overline" color="text" lineHeight={1}>
        Cả chu kỳ (thu mua → xuất kho)
      </MDTypography>
      <MDTypography variant="button" display="block" mt={0.75}>
        {suNhap.khoiLuongThuMua} kg (lúc thu mua) → <b>{suXuat.khoiLuongXuat} kg</b> (lúc xuất)
      </MDTypography>
      <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={0.5}>
        <MDTypography variant="caption" color="text">
          Hao hụt cả chu kỳ:
        </MDTypography>
        <OChenhLech giaTri={chenhLech} />
        <MDTypography variant="caption" color="text">
          ({tyLe > 0 ? "+" : ""}
          {tyLe.toFixed(1)}%)
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

TongQuanChuKy.propTypes = {
  chiTietChuKy: PropTypes.arrayOf(
    PropTypes.shape({
      loai: PropTypes.string,
      khoiLuongThuMua: PropTypes.number,
      khoiLuongXuat: PropTypes.number,
    })
  ).isRequired,
};

function LichSuKho() {
  const fullManHinh = useDialogFullManHinh();
  const { palette } = useTheme();

  // Mặc định luôn là hôm nay — chỉ đổi khi người dùng chủ động truy xuất lại
  // ngày khác, không tự nhớ khoảng ngày đã chọn lần trước.
  const [tuNgay, setTuNgay] = useState(homNay);
  const [denNgay, setDenNgay] = useState(homNay);
  const [nguoiLoc, setNguoiLoc] = useState("");
  const [danhSach, setDanhSach] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");

  // Popup "Chi tiết chu kỳ" — 1 Sọt được tái sử dụng qua nhiều lượt
  // nhập-xuất khác nhau nên phải lọc đúng theo chuKyId (xem
  // lichSuKhoApi.theoChuKy), không lọc theo Sọt (sẽ ra hết lịch sử cũ).
  const [xemChuKy, setXemChuKy] = useState(null);
  const [chiTietChuKy, setChiTietChuKy] = useState(null);
  const [dangTaiChuKy, setDangTaiChuKy] = useState(false);

  function moChiTiet(ls) {
    setXemChuKy(ls);
    if (!ls.chuKyId) {
      // Bản ghi cũ trước khi có mã chu kỳ — chỉ hiển thị được đúng lượt này.
      setChiTietChuKy([ls]);
      return;
    }
    setChiTietChuKy(null);
    setDangTaiChuKy(true);
  }

  useEffect(() => {
    if (!xemChuKy?.chuKyId) return undefined;
    setDangTaiChuKy(true);
    const huy = lichSuKhoApi.langNgheTheoChuKy(
      xemChuKy.chuKyId,
      (ds) => {
        setChiTietChuKy(ds);
        setDangTaiChuKy(false);
      },
      () => setDangTaiChuKy(false)
    );
    return huy;
  }, [xemChuKy?.chuKyId]);

  useEffect(() => {
    if (tuNgay > denNgay) {
      setLoi("Từ ngày phải trước hoặc bằng Đến ngày.");
      return undefined;
    }
    setDangTai(true);
    setLoi("");
    // Đổi khoảng ngày thì bỏ luôn bộ lọc người thực hiện cũ — danh sách người
    // xuất hiện trong khoảng ngày mới có thể khác, giữ lại dễ gây hiểu nhầm
    // là "không có kết quả" trong khi thực ra chỉ do lọc nhầm người.
    setNguoiLoc("");
    const huy = lichSuKhoApi.langNgheTrongKhoang(
      tuNgay,
      denNgay,
      (ds) => {
        setDanhSach(ds);
        setDangTai(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTai(false);
      }
    );
    return huy;
  }, [tuNgay, denNgay]);

  const danhSachNguoi = useMemo(() => {
    const theoId = new Map();
    danhSach.forEach((ls) => {
      if (ls.nguoiThucHien && !theoId.has(ls.nguoiThucHien)) {
        theoId.set(ls.nguoiThucHien, ls.tenNguoiThucHien ?? ls.nguoiThucHien);
      }
    });
    return [...theoId.entries()]
      .map(([id, ten]) => ({ id, ten }))
      .sort((a, b) => a.ten.localeCompare(b.ten, "vi"));
  }, [danhSach]);

  const dsLoc = useMemo(
    () => (nguoiLoc ? danhSach.filter((ls) => ls.nguoiThucHien === nguoiLoc) : danhSach),
    [danhSach, nguoiLoc]
  );

  const soLuotNhap = useMemo(() => dsLoc.filter((ls) => ls.loai === "nhap").length, [dsLoc]);
  const soLuotXuat = useMemo(() => dsLoc.filter((ls) => ls.loai === "xuat").length, [dsLoc]);
  const tongKgNhap = useMemo(
    () =>
      dsLoc
        .filter((ls) => ls.loai === "nhap")
        .reduce((s, ls) => s + (ls.khoiLuongCanLai ?? ls.khoiLuongThuMua), 0),
    [dsLoc]
  );
  const tongKgXuat = useMemo(
    () =>
      dsLoc.filter((ls) => ls.loai === "xuat").reduce((s, ls) => s + (ls.khoiLuongXuat ?? 0), 0),
    [dsLoc]
  );

  // Bảng "Chi tiết từng lượt" gộp nhập+xuất CÙNG 1 chu kỳ (chuKyId) thành 1
  // hàng — trước đây liệt kê thẳng từng document lichSuKho nên 1 Sọt đã
  // nhập rồi xuất hiện ra 2 hàng trùng gần hết cột (thu mua/nhập kho lặp
  // lại), trông như 2 việc khác nhau dù thực ra là 1 chu kỳ. 4 thẻ chỉ số
  // đầu trang vẫn đếm theo dsLoc (từng lượt riêng) — không đổi ý nghĩa "có
  // bao nhiêu LƯỢT nhập/xuất", chỉ gộp ở tầng hiển thị bảng.
  const dsLocGop = useMemo(() => {
    const theoChuKy = new Map();
    const doclap = [];
    dsLoc.forEach((ls) => {
      if (!ls.chuKyId) {
        doclap.push(ls);
        return;
      }
      theoChuKy.set(ls.chuKyId, { ...(theoChuKy.get(ls.chuKyId) ?? {}), [ls.loai]: ls });
    });
    const gop = [...theoChuKy.entries()].map(([chuKyId, { nhap, xuat }]) => ({
      id: chuKyId,
      nhap: nhap ?? null,
      xuat: xuat ?? null,
      thoiGianSapXep: (xuat ?? nhap).thoiGian,
    }));
    const rieng = doclap.map((ls) => ({
      id: ls.id,
      nhap: ls.loai === "nhap" ? ls : null,
      xuat: ls.loai === "xuat" ? ls : null,
      thoiGianSapXep: ls.thoiGian,
    }));
    return [...gop, ...rieng].sort((a, b) => (a.thoiGianSapXep < b.thoiGianSapXep ? 1 : -1));
  }, [dsLoc]);

  useTour("kho-lich-su", dangTai ? [] : buocLichSuKho());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Kho"
          tieuDe="Lịch sử nhập/xuất kho"
          moTa="Từng lượt quét Nhập kho / Xuất kho Sọt, theo khoảng ngày."
        >
          <MDBox
            id="tour-lich-su-kho-bo-loc"
            display="flex"
            flexWrap="wrap"
            alignItems="flex-end"
            gap={2}
          >
            <MDBox
              sx={{
                width: { xs: "calc(50% - 8px)", sm: LAYOUT.filterSelectWidth },
                flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
              }}
            >
              <MDInput
                type="date"
                label="Từ ngày"
                value={tuNgay}
                onChange={(e) => setTuNgay(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox
              sx={{
                width: { xs: "calc(50% - 8px)", sm: LAYOUT.filterSelectWidth },
                flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
              }}
            >
              <MDInput
                type="date"
                label="Đến ngày"
                value={denNgay}
                onChange={(e) => setDenNgay(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            {/* Nhãn caption phía trên + native select — tránh notch MDInput+MenuItem. */}
            <MDBox
              sx={{
                width: { xs: "100%", sm: LAYOUT.filterSelectWidth },
                flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
                alignSelf: "flex-end",
              }}
            >
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                display="block"
                mb={0.5}
                sx={{ lineHeight: 1.2 }}
              >
                Người thực hiện
              </MDTypography>
              <MDInput
                select
                SelectProps={{ native: true }}
                value={nguoiLoc}
                onChange={(e) => setNguoiLoc(e.target.value)}
                fullWidth
                size="small"
              >
                <option value="">Tất cả</option>
                {danhSachNguoi.map((nd) => (
                  <option key={nd.id} value={nd.id}>
                    {nd.ten}
                  </option>
                ))}
              </MDInput>
            </MDBox>
          </MDBox>
        </DauTrang>

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}

        {dangTai ? (
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        ) : (
          <>
            {/* Xanh lá = phía nhập, vàng hạt bơ = phía xuất — bốn chỉ số đọc
                thành hai nhóm thay vì bốn màu rời rạc. */}
            <Grid container spacing={2.5} mb={3}>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Lượt nhập kho"
                  giaTri={soLuotNhap}
                  donVi="trong khoảng đã chọn"
                  icon="move_to_inbox"
                  mau={palette.info.main}
                />
              </Grid>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Khối lượng nhập"
                  giaTri={`${tongKgNhap.toLocaleString("vi-VN")} kg`}
                  donVi="đã cân lúc nhập"
                  icon="scale"
                  mau={palette.info.main}
                />
              </Grid>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Lượt xuất kho"
                  giaTri={soLuotXuat}
                  donVi="trong khoảng đã chọn"
                  icon="local_shipping"
                  mau={palette.gradients.avocado.main}
                />
              </Grid>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Khối lượng xuất"
                  giaTri={`${tongKgXuat.toLocaleString("vi-VN")} kg`}
                  donVi="đã cân lúc xuất"
                  icon="scale"
                  mau={palette.gradients.avocado.main}
                />
              </Grid>
            </Grid>

            <Card id="tour-lich-su-kho-bang">
              <MDBox
                px={2.5}
                py={2}
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <MDTypography variant="h6" component="h2">
                  Chi tiết từng lượt
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {dsLocGop.length} chu kỳ — bấm một dòng để xem chi tiết
                </MDTypography>
              </MDBox>

              {dsLocGop.length === 0 ? (
                <MDBox px={2.5} pb={4} pt={1} textAlign="center">
                  <Icon sx={{ color: SEMANTIC.textDisabled, fontSize: "2.5rem" }}>inventory_2</Icon>
                  <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                    Không có lượt nhập/xuất kho nào.
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                    {nguoiLoc
                      ? "Thử bỏ bộ lọc người thực hiện, hoặc mở rộng khoảng ngày."
                      : "Thử mở rộng khoảng ngày ở đầu trang."}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox sx={{ overflowX: "auto" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={oTieuDeCot}>Mã Sọt</TableCell>
                        <TableCell sx={oTieuDeCot}>Trạng thái</TableCell>
                        <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Thời gian</TableCell>
                        <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                          Thu mua
                        </TableCell>
                        <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                          Nhập kho
                        </TableCell>
                        <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                          Lệch nhập
                        </TableCell>
                        <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                          Xuất kho
                        </TableCell>
                        <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                          Lệch xuất
                        </TableCell>
                        <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Người thực hiện</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dsLocGop.map((nhom) => {
                        const goc = nhom.xuat ?? nhom.nhap;
                        return (
                          <TableRow
                            key={nhom.id}
                            hover
                            onClick={() => moChiTiet(goc)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <MDTypography variant="button" fontWeight="medium" display="block">
                                {goc.maSotHienThi ?? "—"}
                              </MDTypography>
                              <ThongTinPhu nhom={nhom} />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={nhom.xuat ? "Đã xuất kho" : "Nhập kho"}
                                color={nhom.xuat ? "warning" : "success"}
                              />
                            </TableCell>
                            <TableCell sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                              {gioHienThi(goc.thoiGian)}
                            </TableCell>
                            <TableCell align="right" sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                              {goc.khoiLuongThuMua} kg
                            </TableCell>
                            <TableCell align="right" sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                              {goc.khoiLuongCanLai != null ? `${goc.khoiLuongCanLai} kg` : "—"}
                            </TableCell>
                            <TableCell align="right" sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                              <OChenhLech giaTri={nhom.nhap?.chenhLech ?? null} />
                            </TableCell>
                            <TableCell align="right" sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                              {nhom.xuat?.khoiLuongXuat != null
                                ? `${nhom.xuat.khoiLuongXuat} kg`
                                : "—"}
                            </TableCell>
                            <TableCell align="right" sx={[AN_DUOI_MD, { whiteSpace: "nowrap" }]}>
                              <OChenhLech giaTri={nhom.xuat?.chenhLech ?? null} />
                            </TableCell>
                            <TableCell sx={[AN_DUOI_MD, chuXuongDong]}>
                              {tenNguoiGop(nhom.nhap, nhom.xuat)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </MDBox>
              )}
            </Card>
          </>
        )}
      </MDBox>
      <Footer />

      <Dialog
        open={!!xemChuKy}
        onClose={() => setXemChuKy(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="md"
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 6, flexShrink: 0 }}>
          <NhanDau>Chu kỳ kho</NhanDau>
          <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
            Sọt {xemChuKy?.maSotHienThi ?? "—"}
          </MDTypography>
          <IconButton
            onClick={() => setXemChuKy(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        {xemChuKy && (
          <DialogContent sx={oDialogContentFormDai}>
            {!xemChuKy.chuKyId && (
              <MDBox mb={2}>
                <MDAlert color="warning">
                  Bản ghi cũ (trước khi có mã chu kỳ) — chỉ xem được đúng lượt này, không tra ra
                  được lượt nhập/xuất còn lại của Sọt.
                </MDAlert>
              </MDBox>
            )}
            {dangTaiChuKy || !chiTietChuKy ? (
              <MDTypography variant="button" color="text">
                Đang tải...
              </MDTypography>
            ) : (
              <>
                <TongQuanChuKy chiTietChuKy={chiTietChuKy} />
                {chiTietChuKy.map((su, i) => (
                  <MDBox key={su.id}>
                    {i > 0 && <Divider sx={{ my: 0 }} />}
                    <DongSuKien su={su} />
                  </MDBox>
                ))}
                {xemChuKy.chuKyId &&
                  chiTietChuKy.length === 1 &&
                  chiTietChuKy[0].loai === "nhap" && (
                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                      Sọt còn đang nằm trong kho, chưa xuất.
                    </MDTypography>
                  )}
              </>
            )}
          </DialogContent>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default LichSuKho;
